import { memo, useMemo } from "react";
import { MarkdownReact, MESSAGE_PROSE_CLASS } from "@/components/MarkdownReact";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AssetDisplay } from "@/components/AssetDisplay";
import { MessageTimestamp } from "@/components/MessageTimestamp";
import {
  SendRichMessageInputSchema,
  type AgentSdkMessage,
  type OutputAsset,
} from "@forgepoint/shared";

interface RichMessageDisplayProps {
  message: AgentSdkMessage;
  senderName: string;
}

/**
 * Renders a send_rich_message tool call as an inline rich message
 * with interleaved text and asset sections.
 */
export const RichMessageDisplay = memo(function RichMessageDisplay({
  message,
  senderName,
}: RichMessageDisplayProps) {
  const parts = useMemo(() => {
    if (message.message.type !== "tool_call") {
      return null;
    }
    const parsed = SendRichMessageInputSchema.safeParse(
      message.message.arguments,
    );
    return parsed.success ? parsed.data.parts : null;
  }, [message.message]);

  const assetMap = useMemo(() => {
    const map = new Map<string, OutputAsset>();
    for (const asset of message.assets ?? []) {
      map.set(asset.id, asset);
    }
    return map;
  }, [message.assets]);

  const resolveAssets = (assetIds: string[]): OutputAsset[] =>
    assetIds
      .map((id) => assetMap.get(id))
      .filter((asset): asset is OutputAsset => asset !== undefined);

  if (!parts) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 w-full">
      <Avatar className="w-8 h-8">
        <AvatarFallback>{senderName?.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium">{senderName}</div>
          <MessageTimestamp timestamp={new Date(message.createdAt)} />
        </div>
        <div className="mt-1 space-y-2">
          {parts.map((part, index) => {
            if (part.type === "text" && part.text) {
              return (
                <div key={index} className={MESSAGE_PROSE_CLASS}>
                  <MarkdownReact>{part.text}</MarkdownReact>
                </div>
              );
            }

            if (part.type === "assets" && part.asset_ids) {
              const resolvedAssets = resolveAssets(part.asset_ids);
              if (resolvedAssets.length === 0) {
                return null;
              }
              return (
                <div key={index}>
                  <AssetDisplay assets={resolvedAssets} />
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </div>
  );
});
